import { useMemo } from 'react';
import type { ProspectLoan, RateLockPriceAdjustment } from '../../types';
import type { LoanPricingItem } from '@/services/loanPricingService';
import type { LendwiseConcession } from '@/services/lendwiseConcessionService';
import { toNumberOrNull, lenderCreditBreakdown } from '../../utils/loanValueHelpers';
import { computeNetRevenue, type RevenueRow } from '../../utils/netRevenueMath';
import { computeNetLoLocked } from '@/utils/pricing/netLoLocked';

export interface PricingRecalcInput {
  loan: ProspectLoan;
  /**
   * Pricing items read from the source the caller chose — live
   * `loan_pricing_items` in normal mode, `selectedSnapshot.pricingItemsSnapshot`
   * in snapshot mode. The hook does not decide; the caller passes the right set.
   */
  pricingItems: LoanPricingItem[];
  /**
   * Active concessions the caller decided to honor. Snapshot mode typically
   * passes an empty array (historical snapshots don't carry concessions).
   */
  concessions: LendwiseConcession[];
  /**
   * Optional override for the rate-lock adjustments. In snapshot mode the
   * workspace supplies `selectedSnapshot.rateLockFields.rate_lock_buy_side_adjustments`
   * so the ledger math reflects the historical snapshot, not the live loan.
   */
  adjustmentsOverride?: RateLockPriceAdjustment[];
  discountPointPercent?: number | null;
  locrBps?: number | null;
  bpcPercent?: number | null;
  /**
   * Override for Encompass field 4794 only (Non-Specific Lender Credit — the
   * editable portion that the pricing UI writes back). LENPCC is NOT overridable
   * from this hook because it's a read-only aggregation of line items.
   * Phase 2 live preview passes the dirty 4794 value here while LENPCC stays
   * sourced from `loan.le_fields['LENPCC']`.
   */
  lenderCredit4794DollarsOverride?: number | null;
  cureDollars?: number | null;
}

export interface PricingRecalcOutput {
  loanAmount: number;
  /** `rate_lock_total_buy_price` — the price locked after LLPAs are applied. */
  basePriceLocked: number;
  /** Pre-LLPA base price = basePriceLocked − sum of `priceAdjustmentType='BasePrice'` rates. Distinct from Locked; used by the Sankey diagram's Base node. */
  basePriceBeforeLlpa: number;
  /** Sum of `priceAdjustmentType='BasePrice'` rates (signed). Negative when LLPAs reduce the price. */
  totalLlpa: number;
  backEndMargin: number;
  discountPoints: number;
  concessionPoints: number;
  totalLoLocked: number;
  locrPoints: number;
  adjustedLoLocked: number;
  availableLcPts: number;
  /** Encompass field 4794 — Non-Specific Lender Credit (editable, UI writes here). */
  lenderCreditField4794Dollars: number;
  /** LENPCC — Lender-Paid Closing Costs (read-only, aggregated from line items). */
  lenderPaidClosingCostDollars: number;
  /** Total issued lender credit = field4794 + LENPCC (additive). Used by all math. */
  lenderCreditDollars: number;
  /** Total lender credit in points (derived from `lenderCreditDollars`). */
  lenderCreditPoints: number;
  curePoints: number;
  netLoLocked: number;
  investorLockPrice: number;
  companyProfitDollars: number;
  bpcBps: number;
  bonaFide: 'Y' | 'N' | null;
  belowPar: boolean;
  /** Net to Lendwise dollars — updated margin rows excluding LO comp bucket. */
  netToLendwiseDollars: number;
  /** LO compensation in dollars — post-reduction, from the margin stack. */
  loCompensationDollars: number;
  /**
   * BEM expansion rows — one per ProfitMargin adjustment on the loan, INCLUDING
   * LO Compensation (lender-paid LO comp is built into the investor lock price).
   * Each row carries `originalPoints` (fixed at lock) and `updatedPoints` (after
   * concession waterfall absorption + LO comp reduction). Drives the expandable
   * Back-end margin row's detail table.
   */
  bemRows: RevenueRow[];
  /**
   * Fixed built-in back-end stack from Encompass = Σ `|ProfitMargin.rate|` at
   * lock time. Equals `backEndMargin`. Rendered by the collapsed "+ Back-end
   * margin" headline so the Investor Lock Price row reconciles:
   * `basePriceLocked + bemOriginalTotal === investorLockPrice`. Never moves
   * when concessions / LOCR are applied.
   */
  bemOriginalTotal: number;
  /**
   * Current waterfall-reallocated total: concessions absorb into Lendwise
   * margin / Level 1 / Branch Management buckets and LO comp reduction trims
   * the LO Compensation bucket. Rendered in the expansion's Current column
   * and `.bem-foot` subtotal — NOT the collapsed headline. Diverges from
   * `bemOriginalTotal` by exactly the absorbed / reduced amount.
   */
  bemCurrentTotal: number;
  /**
   * True when `bemCurrentTotal !== bemOriginalTotal` (waterfall has moved the
   * stack). Drives the "updated" chip on the collapsed Back-end margin row.
   */
  bemHasUpdates: boolean;
}

/** 1 point = 1% of loan. */
export function pointsToDollarsAt(loanAmount: number, points: number): number {
  return points * loanAmount * 0.01;
}

/** Dollars → points at the current loan amount. */
export function dollarsToPointsAt(loanAmount: number, dollars: number): number {
  if (loanAmount <= 0) {
    return 0;
  }
  return dollars / (loanAmount * 0.01);
}

/**
 * Pure selector for the Product & Pricing workspace.
 *
 * Respects the dual-path rule (memory feedback_pricing_model_dual_path.md):
 * Borrower path (Total Price Locked → Discount → Concessions → Total LO → LO
 * Comp Reduction → Adjusted LO → Lender Credit → Cure → Net LO) and investor
 * path (Total Price Locked → Back-end Margin → Investor Lock Price) branch
 * from the same root — never sequential.
 *
 * Snapshot awareness: the caller is responsible for supplying the right
 * `pricingItems`, `concessions`, and `adjustmentsOverride` for the current
 * view. This hook is a pure function of its inputs.
 */
export function usePricingRecalc(input: PricingRecalcInput): PricingRecalcOutput {
  const {
    loan,
    pricingItems,
    concessions,
    adjustmentsOverride,
    discountPointPercent,
    locrBps: explicitLocrBps,
    bpcPercent,
    lenderCredit4794DollarsOverride,
    cureDollars
  } = input;

  const hasDiscountPointPercentOverride = input.discountPointPercent !== undefined;
  const hasLocrBpsOverride = input.locrBps !== undefined;
  const hasBpcPercentOverride = input.bpcPercent !== undefined;
  const hasLenderCredit4794Override = input.lenderCredit4794DollarsOverride !== undefined;
  const hasCureDollarsOverride = input.cureDollars !== undefined;

  return useMemo(() => {
    const loanAmount =
      toNumberOrNull(loan.rate_lock_base_loan_amount) ?? toNumberOrNull(loan.trans_details_loan_amt) ?? 0;

    const adjustmentsRaw =
      adjustmentsOverride !== undefined
        ? adjustmentsOverride
        : Array.isArray(loan.rate_lock_buy_side_adjustments)
          ? (loan.rate_lock_buy_side_adjustments as RateLockPriceAdjustment[])
          : [];

    const marginAdjustments = adjustmentsRaw.filter((a) => a.priceAdjustmentType === 'ProfitMargin');

    // Back-end margin total (positive magnitude). Encompass stores the margin
    // rates as signed values (negative = lender-paid line item); we derive
    // original margin points via `Math.abs(adj.rate)` and compute the investor
    // lock price as `encompassBuyPrice + originalTotalMarginPoints`.
    let backEndMargin = 0;
    for (const adj of marginAdjustments) {
      const r = Number(adj.rate);
      if (Number.isFinite(r)) {
        backEndMargin += Math.abs(r);
      }
    }

    // rate_lock_total_buy_price is the price locked AFTER LLPAs are applied.
    // Used directly as `basePriceLocked` (legacy parity reference).
    const basePriceLocked = toNumberOrNull(loan.rate_lock_total_buy_price) ?? 0;

    // Sum of `priceAdjustmentType='BasePrice'` rates (signed) — LLPAs before
    // margin. Pre-LLPA base = basePriceLocked − totalLlpa. Matches the
    // breakdown math at PricingBreakdownLedger.tsx:19.
    let totalLlpa = 0;
    for (const adj of adjustmentsRaw) {
      if (adj.priceAdjustmentType === 'BasePrice') {
        const r = Number(adj.rate);
        if (Number.isFinite(r)) {
          totalLlpa += r;
        }
      }
    }
    const basePriceBeforeLlpa = basePriceLocked - totalLlpa;

    const discountRaw = hasDiscountPointPercentOverride
      ? (discountPointPercent ?? null)
      : toNumberOrNull(loan.discount_point_percent);
    const discountPoints = discountRaw ?? 0;

    const bpcRaw = hasBpcPercentOverride
      ? (bpcPercent ?? null)
      : toNumberOrNull(loan.origination_fee_percent);
    const bpcBps = bpcRaw !== null && bpcRaw !== undefined ? bpcRaw * 100 : 0;

    const activeConcessions = concessions.filter((c) => !c.is_legacy);
    const concessionPoints = activeConcessions.reduce(
      (sum, c) => sum + (toNumberOrNull(c.amount_points) ?? 0),
      0
    );

    const storedLocrBps =
      pricingItems.find((i) => i.itemType === 'lo_compensation')?.reductionBps ?? 0;
    const rawLocrBps = hasLocrBpsOverride ? (explicitLocrBps ?? 0) : storedLocrBps;

    // Applied reduction clamps at the Encompass LO-comp base.
    // The absolute-value sum mirrors extractEncompassLoCompBps (rate in points × 100 → bps).
    const encompassLoCompBps = marginAdjustments
      .filter((a) => a.description?.trim().toLowerCase() === 'lo compensation')
      .reduce((sum, a) => sum + Math.abs(Number(a.rate) || 0) * 100, 0);
    const appliedReductionBps = Math.min(rawLocrBps, encompassLoCompBps);
    const locrPoints = appliedReductionBps / 100;

    // Lender credit = field 4794 (editable) + LENPCC (read-only closing-cost
    // aggregation). Additive, not alternatives — see BankerRecon.tsx:917-956.
    // Override substitutes ONLY the 4794 portion; LENPCC stays sourced from
    // loan.le_fields always so Phase 2 live preview can't double-count it.
    const lcBreakdown = lenderCreditBreakdown(loan);
    const lenderCreditField4794Dollars = hasLenderCredit4794Override
      ? (lenderCredit4794DollarsOverride ?? 0)
      : lcBreakdown.nonSpecific;
    const lenderPaidClosingCostDollars = lcBreakdown.lenderPaidCC;
    const lenderCreditDollars = lenderCreditField4794Dollars + lenderPaidClosingCostDollars;
    const cureRaw = hasCureDollarsOverride
      ? (cureDollars ?? 0)
      : toNumberOrNull(loan.cure_applied_lender_credit) ?? 0;
    const lenderCreditPoints = dollarsToPointsAt(loanAmount, lenderCreditDollars);
    const curePoints = dollarsToPointsAt(loanAmount, cureRaw);

    // Net LO Locked waterfall — shared pure helper, reused by the LO self-service
    // "My Commissions" report so its LO Profitability matches this UI exactly.
    const { totalLoLocked, adjustedLoLocked, netLoLocked, displayedNetLoLocked, loProfitDollars } =
      computeNetLoLocked({
        basePriceLocked,
        discountPoints,
        concessionPoints,
        locrPoints,
        lenderCreditPoints,
        curePoints,
        loanAmount
      });
    const availableLcPts = Math.max(adjustedLoLocked - 100, 0);
    const investorLockPrice = basePriceLocked + backEndMargin;
    // Above-par excess attributed to the LO ("LO Profitability").
    const companyProfitDollars = loProfitDollars;

    const bonaFide: 'Y' | 'N' | null =
      discountRaw === null || discountRaw === undefined ? null : discountRaw > 0 ? 'Y' : 'N';

    // Match the displayed Net LO Locked precision. If the screen shows 100.000,
    // the save guard should not block on sub-decimal floating/points dust.
    const belowPar = displayedNetLoLocked < 100;

    // Canonical Net to Lendwise + effective LO comp dollars come from the
    // shared netRevenueMath selector so the KPI strip and ledger agree.
    const netRevenue = computeNetRevenue({
      marginAdjustments,
      activeConcessions,
      appliedReductionBps,
      loanAmount
    });

    // BEM (Back-End Margin) breakdown — INCLUDES all ProfitMargin rows
    // (Lendwise Margin, Level 1, Branch Mgmt, Dynamic Markups, LO Compensation).
    // `bemOriginalTotal` = fixed built-in stack Encompass ships at lock — what
    // the collapsed "+ Back-end margin" row renders so arithmetic reconciles
    // with Investor Lock Price. `bemCurrentTotal` = the same stack after
    // concession absorption + LO comp reduction re-slice it; rendered in the
    // expansion's Current column and `.bem-foot` subtotal only.
    const bemRows = netRevenue.rows;
    const bemOriginalTotal = bemRows.reduce((s, r) => s + r.originalPoints, 0);
    const bemCurrentTotal = bemRows.reduce((s, r) => s + r.updatedPoints, 0);
    const bemHasUpdates = Math.abs(bemCurrentTotal - bemOriginalTotal) >= 0.0005;

    return {
      loanAmount,
      basePriceLocked,
      basePriceBeforeLlpa,
      totalLlpa,
      backEndMargin,
      discountPoints,
      concessionPoints,
      totalLoLocked,
      locrPoints,
      adjustedLoLocked,
      availableLcPts,
      lenderCreditField4794Dollars,
      lenderPaidClosingCostDollars,
      lenderCreditDollars,
      lenderCreditPoints,
      curePoints,
      netLoLocked,
      investorLockPrice,
      companyProfitDollars,
      bpcBps,
      bonaFide,
      belowPar,
      netToLendwiseDollars: netRevenue.netToLendwiseDollars,
      loCompensationDollars: netRevenue.loCompensationDollars,
      bemRows,
      bemOriginalTotal,
      bemCurrentTotal,
      bemHasUpdates
    };
  }, [
    loan,
    pricingItems,
    concessions,
    adjustmentsOverride,
    discountPointPercent,
    hasDiscountPointPercentOverride,
    explicitLocrBps,
    hasLocrBpsOverride,
    bpcPercent,
    hasBpcPercentOverride,
    lenderCredit4794DollarsOverride,
    hasLenderCredit4794Override,
    cureDollars,
    hasCureDollarsOverride
  ]);
}
